import _ from 'lodash';
import React, {useCallback} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {createSelector} from 'reselect';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {AsyncData, asyncDataLoaded} from '../../../../../helpers/asyncDataHelpers';
import {ResolveDispatchProps} from '../../../../../helpers/reduxHelpers';
import {makeMapStateToProps, ResolveStateProps} from '../../../../../helpers/reselectHelpers';
import {ActivityModel} from '../../../../../models/api/activityModel';
import {AppState} from '../../../../../store/appState';
import {
  pinActivityAction,
  unpinActivityAction,
} from '../../../../../store/data/conversations/pinActivitiesToConversationsActions';
import {getActivities} from '../../../../../store/data/dataSelectors';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {jumpToActivityAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {
  ConversationSummaryPinnedActivities,
  ConversationSummaryPinnedActivityClickHandler,
} from './conversationSummaryPinnedActivities';
import {
  withUnpinnedActivityTracking,
  WithUnpinnedActivityTrackingInjectedProps,
} from './withUnpinnedActivityTracking';

/*
 * Props + Types.
 */

export interface ConversationSummaryPinnedActivitiesContainerPublicProps {
  panelId: ViewerPanelId;
  pinnedActivities: AsyncData<ReadonlyArray<ActivityModel>>;
  onPinnedActivitiesRetryClick: Handler;
  onRequestClose: Handler;
}

type ConversationSummaryPinnedActivitiesContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type ConversationSummaryPinnedActivitiesContainerDispatchProps = ResolveDispatchProps<typeof dispatchProps>;

type ConversationSummaryPinnedActivitiesContainerProps =
  ConversationSummaryPinnedActivitiesContainerPublicProps &
    ConversationSummaryPinnedActivitiesContainerStateProps &
    ConversationSummaryPinnedActivitiesContainerDispatchProps &
    WithUnpinnedActivityTrackingInjectedProps;

/*
 * Component.
 */

const ConversationSummaryPinnedActivitiesContainerComponent: React.FC<
  ConversationSummaryPinnedActivitiesContainerProps
> = (props) => {
  const {
    panelId,
    pinnedActivities,
    onRequestClose,
    recentlyUnpinnedActivities,
    markActivityAsUnpinned,
    jumpToActivity,
    pinActivity,
    unpinActivity,
  } = props;

  const pinnedOrRecentlyUnpinnedActivities =
    pinnedActivities.status === AsyncStatusesEnum.LOADED
      ? asyncDataLoaded(
          collectPinnedOrRecentlyUnpinnedActivities(pinnedActivities.data, recentlyUnpinnedActivities),
        )
      : pinnedActivities;

  const onJumpClick: ConversationSummaryPinnedActivityClickHandler = useCallback(
    (activityId) => {
      jumpToActivity(panelId, {id: activityId});
      onRequestClose();
    },
    [panelId, jumpToActivity, onRequestClose],
  );

  const onPinClick: ConversationSummaryPinnedActivityClickHandler = useCallback(
    (activityId, conversationId) => {
      pinActivity(activityId, conversationId);
    },
    [pinActivity],
  );

  const onUnpinClick: ConversationSummaryPinnedActivityClickHandler = useCallback(
    (activityId, conversationId) => {
      markActivityAsUnpinned(activityId);
      unpinActivity(activityId, conversationId);
    },
    [markActivityAsUnpinned, unpinActivity],
  );

  return (
    <ConversationSummaryPinnedActivities
      pinnedOrRecentlyUnpinnedActivities={pinnedOrRecentlyUnpinnedActivities}
      onPinnedActivitiesRetryClick={props.onPinnedActivitiesRetryClick}
      panelId={props.panelId}
      onJumpClick={onJumpClick}
      onPinClick={onPinClick}
      onUnpinClick={onUnpinClick}
    />
  );
};

// Note that there is no guarantee that any recentlyUnpinnedActivities are actually unpinned,
// as the user could 'unpin' and then 'repin'. This means there may be duplicates between
// `pinnedActivities` and `recentlyUnpinnedActivities`, but this is not a problem.
//
// It IS guaranteed that while the ConversationSummaryPinnedActivitiesContainer is showing,
// any activities that were unpinned by the user will be present in recentlyUnpinnedActivities.
// This all ensures that when the user unpins an activity, the tombstone is able to show.
function collectPinnedOrRecentlyUnpinnedActivities(
  pinnedActivities: ReadonlyArray<ActivityModel>,
  recentlyUnpinnedActivities: ReadonlyArray<ActivityModel>,
): ReadonlyArray<ActivityModel> {
  return _([...pinnedActivities, ...recentlyUnpinnedActivities])
    .uniqBy('id')
    .sortBy('date')
    .value();
}

/*
 * Selectors.
 */

function getRecentlyUnpinnedActivityIds(
  _state: AppState,
  props: ConversationSummaryPinnedActivitiesContainerConnectProps,
) {
  return props.unpinnedActivityIds;
}

function makeGetRecentlyUnpinnedActivities() {
  return createSelector(
    [getActivities, getRecentlyUnpinnedActivityIds],
    (activities, unpinnedActivityIds) =>
      _(activities).pick(unpinnedActivityIds).values().value() as ReadonlyArray<ActivityModel>,
  );
}

/*
 * Redux.
 */

type ConversationSummaryPinnedActivitiesContainerConnectProps =
  ConversationSummaryPinnedActivitiesContainerPublicProps & WithUnpinnedActivityTrackingInjectedProps;

const mapStateToProps = makeMapStateToProps<ConversationSummaryPinnedActivitiesContainerConnectProps>()(
  () => ({
    recentlyUnpinnedActivities: makeGetRecentlyUnpinnedActivities(),
  }),
);

const dispatchProps = {
  jumpToActivity: jumpToActivityAction,
  pinActivity: pinActivityAction,
  unpinActivity: unpinActivityAction,
};

export const ConversationSummaryPinnedActivitiesContainer = compose(
  withUnpinnedActivityTracking,
  connect(mapStateToProps, dispatchProps),
)(
  ConversationSummaryPinnedActivitiesContainerComponent,
) as React.ComponentType<ConversationSummaryPinnedActivitiesContainerPublicProps>;
